import { Conversation } from '@twilio/conversations'
import get from 'lodash/get'

import { Marketplace, UserType } from 'src/types'
import { EnhancedConversationContactAggregated } from 'src/types/conversationContacts'

export const calculateConversationUnreadCount = (conversation: Conversation) => {
  const lastReadMessageIndex = conversation.lastReadMessageIndex
  const lastMessageIndex = conversation.lastMessage?.index

  if (lastReadMessageIndex !== null && lastMessageIndex !== undefined) {
    return lastMessageIndex - lastReadMessageIndex
  }

  if (lastMessageIndex !== undefined) {
    return lastMessageIndex + 1
  }

  return 0
}

interface ConversationCanBeDisplayedArgs {
  conversation: Conversation
  contacts: EnhancedConversationContactAggregated[]
  currentMarketplaceId?: Id
  ecosystems?: Marketplace[]
}

export const conversationCanBeDisplayed = ({
  conversation,
  contacts,
  currentMarketplaceId,
  ecosystems = [],
}: ConversationCanBeDisplayedArgs) => {
  const contactCompanyId = currentMarketplaceId
    ? (get(conversation, 'attributes.seller_company_id') as Id | undefined)
    : (get(conversation, 'attributes.buyer_company_id') as Id | undefined)
  const channelContactIsReachable = contacts.some(contact => contact.id === contactCompanyId)
  const availableMarketplaceIds = currentMarketplaceId
    ? [currentMarketplaceId]
    : ecosystems.map(({ id }) => id)
  const channelIsOnCorrectMarketplace = availableMarketplaceIds.some(
    availableMarketplaceId =>
      availableMarketplaceId === get(conversation, 'attributes.marketplace_id')
  )

  return channelContactIsReachable && channelIsOnCorrectMarketplace
}

interface EnhanceConversationArgs {
  conversation: Conversation
  contacts: EnhancedConversationContactAggregated[]
  userType: UserType
}

interface EnhancedConversationResult {
  conversation: Conversation
  unreadCount: number
  userName: string
}

export const enhanceConversation = ({
  conversation,
  contacts,
  userType,
}: EnhanceConversationArgs): EnhancedConversationResult => {
  const contactCompanyId =
    userType === UserType.BUYER
      ? (get(conversation, 'attributes.seller_company_id') as Id | undefined)
      : (get(conversation, 'attributes.buyer_company_id') as Id | undefined)
  const contactCompany = contacts.find(contact => contact.id === contactCompanyId)

  return {
    conversation,
    unreadCount: calculateConversationUnreadCount(conversation),
    userName: contactCompany?.name ?? contactCompanyId?.toString() ?? '',
  }
}

type FormatConversationsArgs = Omit<ConversationCanBeDisplayedArgs, 'conversation'> & {
  conversations: Conversation[]
}

export const formatConversations = ({
  conversations,
  contacts,
  currentMarketplaceId,
  ecosystems = [],
}: FormatConversationsArgs) =>
  conversations
    .filter(conversation =>
      conversationCanBeDisplayed({
        conversation,
        contacts,
        currentMarketplaceId,
        ecosystems,
      })
    )
    .map(conversation =>
      enhanceConversation({
        conversation,
        contacts,
        userType: currentMarketplaceId ? UserType.BUYER : UserType.SELLER,
      })
    )
